/**
 * Package Verification Page
 * Display package details (case/pallet) with contents and transfer options
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { packagingService, Package, PackageContents } from '../services/packaging.service';
import { Button, Alert, Input, Modal } from '../components/ui';
import PackageTransferModal from '../components/PackageTransferModal';

export default function PackageVerificationPage() {
  const { qrToken } = useParams<{ qrToken: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [packageData, setPackageData] = useState<Package | null>(null);
  const [contents, setContents] = useState<PackageContents | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Dealer activation state
  const [showDealerActivation, setShowDealerActivation] = useState(false);
  const [dealerId, setDealerId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpExpiryTime, setOtpExpiryTime] = useState<Date | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(3);

  useEffect(() => {
    if (qrToken) {
      loadPackageData();
    }
  }, [qrToken]);

  const loadPackageData = async () => {
    if (!qrToken) return;

    try {
      setLoading(true);
      setError(null);

      const [pkgResult, contentsResult] = await Promise.all([
        packagingService.getPackageByQR(qrToken),
        packagingService.getPackageContents(qrToken),
      ]);

      if (pkgResult.success && pkgResult.data) {
        setPackageData(pkgResult.data);
      } else {
        setError(pkgResult.error || 'Package not found');
      }

      if (contentsResult.success && contentsResult.data) {
        setContents(contentsResult.data);
      }
    } catch (err: any) {
      console.error('Failed to load package:', err);
      setError(err.response?.data?.error || 'Failed to load package information');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateAll = async () => {
    if (!qrToken || !packageData || !contents) return;

    const message = `Are you sure you want to activate all ${contents.totalUnits} products in this ${packageData.packageType.toLowerCase()}?`;
    
    if (!confirm(message)) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const result = await packagingService.activatePackage(qrToken);

      if (result.success) {
        await loadPackageData();
        alert(`Successfully activated ${result.results.newlyActivated} products! (${result.results.alreadyActivated} were already activated)`);
      } else {
        setError(result.error || 'Failed to activate products');
      }
    } catch (err: any) {
      console.error('Failed to activate package:', err);
      setError(err.response?.data?.error || 'Failed to activate products');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBreakdown = async () => {
    if (!qrToken || !packageData) return;

    if (packageData.packageType === 'PALLET') {
      alert('Pallet breakdown is not supported in MVP. Please break down individual cases.');
      return;
    }

    if (!confirm('Are you sure you want to unpack this case? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const result = await packagingService.breakdownPackage(qrToken);

      if (result.success) {
        await loadPackageData();
        alert('Package marked as unpacked successfully!');
      } else {
        setError(result.error || 'Failed to unpack package');
      }
    } catch (err: any) {
      console.error('Failed to breakdown package:', err);
      setError(err.response?.data?.error || 'Failed to unpack package');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferSuccess = () => {
    setShowTransferModal(false);
    loadPackageData();
  };

  const handleRequestOTP = async () => {
    if (!qrToken || !dealerId.trim()) {
      setError('Please enter your dealer ID');
      return;
    }

    try {
      setOtpLoading(true);
      setError(null);

      const result = await packagingService.requestPackageActivationOTP(qrToken, dealerId);

      if (result.success) {
        setOtpRequested(true);
        setOtpExpiryTime(result.expiryTime ? new Date(result.expiryTime) : null);
        setAttemptsRemaining(result.attemptsRemaining || 3);
        setSuccess('OTP sent to your registered mobile number. Please enter the code below.');
      } else {
        setError(result.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      console.error('Failed to request OTP:', err);
      setError('Failed to request OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!qrToken || !dealerId.trim() || !otpCode.trim()) {
      setError('Please enter the OTP code');
      return;
    }

    try {
      setOtpLoading(true);
      setError(null);

      const result = await packagingService.verifyPackageActivationOTP(qrToken, dealerId, otpCode);

      if (result.success && result.packageActivated) {
        setSuccess(result.message || 'Package activated successfully!');
        setShowDealerActivation(false);
        setOtpRequested(false);
        setOtpCode('');
        setDealerId('');
        // Reload package data to show updated activation status
        await loadPackageData();
      } else {
        setError(result.error || 'Invalid OTP code');
        setAttemptsRemaining(result.attemptsRemaining || 0);
      }
    } catch (err: any) {
      console.error('Failed to verify OTP:', err);
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCloseDealerActivation = () => {
    setShowDealerActivation(false);
    setOtpRequested(false);
    setOtpCode('');
    setDealerId('');
    setError(null);
    setSuccess(null);
  };

  const isOwner = user && packageData && user.id === packageData.currentOwnerId;
  const canTransfer = isOwner && packageData?.status === 'active';
  const canBreakdown = isOwner && packageData?.status === 'active' && packageData?.packageType === 'CASE';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Package Verification</h1>
              <p className="text-sm text-gray-600 mt-1">View package details and contents</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : success ? (
          <Alert variant="success">{success}</Alert>
        ) : packageData ? (
          <div className="space-y-6">
            {/* Package Type Badge */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`px-4 py-2 rounded-lg font-bold text-white ${
                      packageData.packageType === 'PALLET' ? 'bg-purple-600' : 'bg-blue-600'
                    }`}
                  >
                    {packageData.packageType}
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      packageData.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {packageData.status === 'active' ? 'Active' : 'Unpacked'}
                  </span>
                </div>
              </div>

              {/* Package Details */}
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-600">QR Token</dt>
                  <dd className="text-lg font-mono text-gray-900 mt-1">{packageData.qrToken}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Created</dt>
                  <dd className="text-lg text-gray-900 mt-1">
                    {new Date(packageData.createdAt).toLocaleDateString()}
                  </dd>
                </div>
                {packageData.brokenDownAt && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-gray-600">Unpacked On</dt>
                    <dd className="text-lg text-gray-900 mt-1">
                      {new Date(packageData.brokenDownAt).toLocaleString()}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Contents */}
            {contents && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Package Contents</h3>

                {/* Total Units */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-900 font-medium">Total Units</span>
                    <span className="text-3xl font-bold text-blue-600">{contents.totalUnits}</span>
                  </div>
                </div>

                {/* Child Packages */}
                {contents.childPackages && contents.childPackages.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Child Packages ({contents.childPackages.length})
                    </h4>
                    <div className="space-y-2">
                      {contents.childPackages.map((child) => (
                        <div
                          key={child.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`px-3 py-1 rounded text-sm font-medium text-white ${
                                child.packageType === 'CASE' ? 'bg-blue-600' : 'bg-purple-600'
                              }`}
                            >
                              {child.packageType}
                            </span>
                            <span className="text-sm font-mono text-gray-600">{child.qrToken}</span>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              child.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {child.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products */}
                {contents.products && contents.products.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Products ({contents.products.length})
                    </h4>
                    <p className="text-sm text-gray-600">
                      This {packageData.packageType.toLowerCase()} contains {contents.products.length}{' '}
                      individual product units.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Breakdown Warning */}
            {packageData.status === 'broken_down' && (
              <Alert variant="warning">
                This package has been unpacked. Individual child packages or products can now be
                transferred separately.
              </Alert>
            )}

            {/* Action Buttons */}
            {user && (
              <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                  {isOwner && packageData.status === 'active' && (
                    <Button
                      fullWidth
                      variant="primary"
                      onClick={handleActivateAll}
                      disabled={actionLoading}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Activate All Products ({contents?.totalUnits || 0})
                    </Button>
                  )}

                  {canTransfer && (
                    <Button fullWidth onClick={() => setShowTransferModal(true)} disabled={actionLoading}>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        />
                      </svg>
                      Transfer Package
                    </Button>
                  )}

                  {canBreakdown && (
                    <Button
                      fullWidth
                      variant="secondary"
                      onClick={handleBreakdown}
                      disabled={actionLoading}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                      Unpack Case
                    </Button>
                  )}
                </div>

                {/* Dealer Activation Section */}
                {!user && packageData.status === 'active' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-blue-900">Dealer Activation</h4>
                        <p className="text-xs text-blue-700">Activate products in this package with OTP verification</p>
                      </div>
                    </div>
                    <Button
                      fullWidth
                      variant="primary"
                      onClick={() => setShowDealerActivation(true)}
                      disabled={actionLoading}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Activate as Dealer
                    </Button>
                  </div>
                )}

                {!isOwner && user && (
                  <Alert variant="info">
                    You are not the owner of this package. Only the current owner can activate, transfer, or unpack it.
                  </Alert>
                )}
              </div>
            )}
          </div>
        ) : (
          <Alert variant="error">Package not found</Alert>
        )}
      </main>

      {/* Transfer Modal */}
      {showTransferModal && packageData && contents && (
        <PackageTransferModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          packageData={packageData}
          totalUnits={contents.totalUnits}
          onSuccess={handleTransferSuccess}
        />
      )}

      {/* Dealer Activation Modal */}
      {showDealerActivation && (
        <Modal
          isOpen={showDealerActivation}
          onClose={handleCloseDealerActivation}
          title="Dealer Package Activation"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                🔐 Enter your dealer ID to receive an OTP for package activation. The OTP will be sent to your registered mobile number.
              </p>
            </div>

            {error && (
              <Alert variant="error">{error}</Alert>
            )}

            {success && (
              <Alert variant="success">{success}</Alert>
            )}

            {!otpRequested ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="dealerId" className="block text-sm font-medium text-gray-700 mb-2">
                    Dealer ID
                  </label>
                  <Input
                    id="dealerId"
                    type="text"
                    value={dealerId}
                    onChange={(e) => setDealerId(e.target.value)}
                    placeholder="Enter your dealer ID"
                    disabled={otpLoading}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCloseDealerActivation}
                    disabled={otpLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRequestOTP}
                    disabled={!dealerId.trim() || otpLoading}
                  >
                    {otpLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    📱 OTP sent to your registered mobile number. Please enter the 6-digit code below.
                  </p>
                  {otpExpiryTime && (
                    <p className="text-xs text-green-700 mt-1">
                      Expires at: {otpExpiryTime.toLocaleTimeString()}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="otpCode" className="block text-sm font-medium text-gray-700 mb-2">
                    OTP Code
                  </label>
                  <Input
                    id="otpCode"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    disabled={otpLoading}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Attempts remaining: {attemptsRemaining}
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOtpRequested(false);
                      setOtpCode('');
                    }}
                    disabled={otpLoading}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleVerifyOTP}
                    disabled={otpCode.length !== 6 || otpLoading}
                  >
                    {otpLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </>
                    ) : (
                      'Activate Package'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
